/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  AgendaService,
  ActivityPlan,
  Department,
  User,
  MaintenanceUnit,
  AreaGroup,
  AreaSubgroup,
  Area,
  EquipmentType,
  Equipment,
} from 'services/reports';
import { AgendaTask } from '../utils/eventUtils';
import { useHeaderFilters } from 'context/GlobalFiltersContext';
import { EventsByDate, transformAgendaTasksToEvents } from '../utils/eventUtils';
import { buildApiPayload } from '../utils/payloadUtils';
import { startOfWeek } from 'date-fns';
import { CalendarView } from './useCalendarNavigation';
import * as Sentry from '@sentry/react';
export type EventType = 'pendentes' | 'baixadas';

interface FilterOption {
  label: string;
  value: string;
  foto?: string;
}

interface CalendarFields {
  planos: FilterOption[];
  executores: FilterOption[];
  setores: FilterOption[];
}

interface UseCalendarFiltersParams {
  currentDate: Date;
  view: CalendarView;
}

interface UseCalendarFiltersReturn {
  type: EventType;
  setType: (type: EventType) => void;
  planos: FilterOption[];
  setPlanos: (planos: FilterOption[]) => void;
  planosSelectionLabel: string;
  executoresSetoresSelectionLabel: string;
  ambientesEquipamentosSelectionLabel: string;
  planosValues: string[];
  handlePlanosChange: (values: unknown) => void;
  planosOptions: FilterOption[];
  ocorrencias: FilterOption[];
  setOcorrencias: (ocorrencias: FilterOption[]) => void;
  executores: FilterOption[];
  setExecutores: (executores: FilterOption[] | undefined) => void;
  executoresValues: string[];
  handleExecutoresChange: (values: unknown) => void;
  executoresOptions: FilterOption[];
  tempExecutoresValues: string[];
  handleTempExecutoresChange: (values: unknown) => void;
  setores: FilterOption[];
  setSetores: (setores: FilterOption[] | undefined) => void;
  setoresValues: string[];
  handleSetoresChange: (values: unknown) => void;
  setoresOptions: FilterOption[];
  tempSetoresValues: string[];
  handleTempSetoresChange: (values: unknown) => void;
  applyExecutoresSetoresFilters: () => void;
  clearExecutoresSetoresFilters: () => void;
  fields: CalendarFields;
  openFilters: boolean;
  setOpenFilters: (open: boolean) => void;
  executoresSetoresModalOpen: boolean;
  setExecutoresSetoresModalOpen: (open: boolean) => void;
  onExecutoresSearch: (term: string) => void;
  ambientesEquipamentosModalOpen: boolean;
  setAmbientesEquipamentosModalOpen: (open: boolean) => void;
  planosModalOpen: boolean;
  setPlanosModalOpen: (open: boolean) => void;
  tempPlanosValues: string[];
  handleTempPlanosChange: (values: unknown) => void;
  applyPlanosFilters: () => void;
  clearPlanosFilters: () => void;
  maintenanceUnitsOptions: FilterOption[];
  maintenanceUnitsValues: string[];
  handleMaintenanceUnitsChange: (values: unknown) => void;
  areaGroupsOptions: FilterOption[];
  areaGroupsValues: string[];
  handleAreaGroupsChange: (values: unknown) => void;
  tempAreaGroupsValues: string[];
  handleTempAreaGroupsChange: (values: unknown) => void;
  areaSubgroupsOptions: FilterOption[];
  areaSubgroupsValues: string[];
  handleAreaSubgroupsChange: (values: unknown) => void;
  tempAreaSubgroupsValues: string[];
  handleTempAreaSubgroupsChange: (values: unknown) => void;
  areasOptions: FilterOption[];
  areasValues: string[];
  handleAreasChange: (values: unknown) => void;
  tempAreasValues: string[];
  handleTempAreasChange: (values: unknown) => void;
  equipmentTypesOptions: FilterOption[];
  equipmentTypesValues: string[];
  handleEquipmentTypesChange: (values: unknown) => void;
  tempEquipmentTypesValues: string[];
  handleTempEquipmentTypesChange: (values: unknown) => void;
  equipmentOptions: FilterOption[];
  equipmentValues: string[];
  handleEquipmentChange: (values: unknown) => void;
  tempEquipmentValues: string[];
  handleTempEquipmentChange: (values: unknown) => void;
  applyAmbientesEquipamentosFilters: () => void;
  clearAmbientesEquipamentosFilters: () => void;
  hasActiveFilters: boolean;
  clearAllFilters: () => void;
  events: EventsByDate;
  isLoadingEvents: boolean;
  fetchAgendaTasks: (isWeekView?: boolean, overrides?: { planosValues?: string[] }) => Promise<void>;
  setWeekView: (isWeekView: boolean) => void;
  loadMoreAreas: () => Promise<void>;
  searchArea: (term: string) => void;
  loadMoreEquipamentos: () => Promise<void>;
  searchEquipamento: (term: string) => void;
}

export const useCalendarFilters = (params?: UseCalendarFiltersParams): UseCalendarFiltersReturn => {
  const { currentDate = new Date(), view } = params || {};

  const effectiveDate = useMemo(() => {
    if (!currentDate) return startOfWeek(new Date(), { weekStartsOn: 0 });
    if (view === undefined || view === CalendarView.Semana) {
      return currentDate;
    }
    return currentDate;
  }, [currentDate, view]);
  const [type, setType] = useState<EventType>('pendentes');
  const [planos, setPlanos] = useState<FilterOption[]>([]);
  const [planosOptions, setPlanosOptions] = useState<FilterOption[]>([]);
  const [ocorrencias, setOcorrencias] = useState<FilterOption[]>([]);
  const [executores, setExecutoresState] = useState<FilterOption[]>([]);
  const [executoresOptions, setExecutoresOptions] = useState<FilterOption[]>([]);
  const [tempExecutores, setTempExecutores] = useState<FilterOption[]>([]);
  const [setores, setSetoresState] = useState<FilterOption[]>([]);
  const [setoresOptions, setSetoresOptions] = useState<FilterOption[]>([]);
  const [tempSetores, setTempSetores] = useState<FilterOption[]>([]);
  const [openFilters, setOpenFilters] = useState<boolean>(true);
  const [executoresSetoresModalOpen, setExecutoresSetoresModalOpen] = useState<boolean>(false);
  const [ambientesEquipamentosModalOpen, setAmbientesEquipamentosModalOpen] = useState<boolean>(false);
  const [planosModalOpen, setPlanosModalOpen] = useState<boolean>(false);
  const [tempPlanos, setTempPlanos] = useState<FilterOption[]>([]);
  const [maintenanceUnitsOptions, setMaintenanceUnitsOptions] = useState<FilterOption[]>([]);
  const [maintenanceUnits, setMaintenanceUnits] = useState<FilterOption[]>([]);
  const [areaGroupsOptions, setAreaGroupsOptions] = useState<FilterOption[]>([]);
  const [areaGroups, setAreaGroups] = useState<FilterOption[]>([]);
  const [tempAreaGroups, setTempAreaGroups] = useState<FilterOption[]>([]);
  const [areaSubgroupsOptions, setAreaSubgroupsOptions] = useState<FilterOption[]>([]);
  const [areaSubgroups, setAreaSubgroups] = useState<FilterOption[]>([]);
  const [tempAreaSubgroups, setTempAreaSubgroups] = useState<FilterOption[]>([]);
  const [areasOptions, setAreasOptions] = useState<FilterOption[]>([]);
  const [areas, setAreas] = useState<FilterOption[]>([]);
  const [tempAreas, setTempAreas] = useState<FilterOption[]>([]);
  const [equipmentTypesOptions, setEquipmentTypesOptions] = useState<FilterOption[]>([]);
  const [equipmentTypes, setEquipmentTypes] = useState<FilterOption[]>([]);
  const [tempEquipmentTypes, setTempEquipmentTypes] = useState<FilterOption[]>([]);
  const equipmentTypesDataRef = useRef<EquipmentType[]>([]);
  const [equipmentOptions, setEquipmentOptions] = useState<FilterOption[]>([]);
  const [equipment, setEquipment] = useState<FilterOption[]>([]);
  const [tempEquipment, setTempEquipment] = useState<FilterOption[]>([]);
  const [events, setEvents] = useState<EventsByDate>({});
  const [isLoadingEvents, setIsLoadingEvents] = useState<boolean>(false);
  const isWeekViewRef = useRef<boolean>(true);
  const lastFetchParamsRef = useRef<string>('');
  const previousFiltersKeyRef = useRef<string>('');
  const isMountedRef = useRef<boolean>(true);
  const [areaPage, setAreaPage] = useState<number>(1);
  const [areaSearch, setAreaSearch] = useState<string>('');
  const [equipamentoPage, setEquipamentoPage] = useState<number>(1);
  const [equipamentoSearch, setEquipamentoSearch] = useState<string>('');
  const debounceAreaRef = useRef<NodeJS.Timeout | null>(null);
  const debounceEquipamentoRef = useRef<NodeJS.Timeout | null>(null);
  const debounceExecutoresSearchRef = useRef<NodeJS.Timeout | null>(null);
  const initFromGlobalDoneForExecutoresSetoresRef = useRef<boolean>(false);

  const { headerFiltersContext } = useHeaderFilters();
  const { headerFilters } = headerFiltersContext;
  const empresaAttrs = useMemo(
    () => ({ empresa_id: headerFilters?.companies?.id, empresa_name: headerFilters?.companies?.name }),
    [headerFilters?.companies?.id, headerFilters?.companies?.name],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Carrega unidades para o campo Unidades da tela quando há empresa selecionada
  useEffect(() => {
    if (headerFilters?.companies?.id) {
      fetchMaintenanceUnits();
    }
  }, [
    headerFilters?.companies?.id,
    headerFilters?.units?.id,
    headerFilters?.unitGroups?.id,
    headerFilters?.unitSubgroups?.id,
    headerFilters?.states?.id,
    headerFilters?.cities?.id,
    headerFilters?.providers?.id,
  ]);

  // Sincroniza: se houver unidade marcada no filtro global, ela deve aparecer marcada no filtro Unidades da Agenda
  useEffect(() => {
    const globalUnitId = headerFilters?.units?.id;
    if (!globalUnitId || maintenanceUnitsOptions.length === 0) return;
    const globalUnitOption = maintenanceUnitsOptions.find(opt => opt.value === String(globalUnitId));
    if (!globalUnitOption) return;
    setMaintenanceUnits(prev => {
      const alreadySelected = prev.some(opt => opt.value === String(globalUnitId));
      if (alreadySelected) return prev;
      return [...prev, globalUnitOption];
    });
  }, [headerFilters?.units?.id, maintenanceUnitsOptions]);

  const currentDateString = useMemo(() => {
    if (!effectiveDate) return undefined;
    const year = effectiveDate.getFullYear();
    const month = String(effectiveDate.getMonth() + 1).padStart(2, '0');
    const day = String(effectiveDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, [effectiveDate?.getTime()]);

  const hasActiveFilters = useMemo(
    () =>
      planos.length > 0 ||
      executores.length > 0 ||
      setores.length > 0 ||
      maintenanceUnits.length > 0 ||
      areaGroups.length > 0 ||
      areaSubgroups.length > 0 ||
      areas.length > 0 ||
      equipmentTypes.length > 0 ||
      equipment.length > 0 ||
      type !== 'pendentes',
    [planos, executores, setores, maintenanceUnits, areaGroups, areaSubgroups, areas, equipmentTypes, equipment, type],
  );

  const fields = useMemo<CalendarFields>(
    () => ({
      planos: planosOptions,
      executores: executoresOptions,
      setores: setoresOptions,
    }),
    [planosOptions, executoresOptions, setoresOptions],
  );

  const planosSelectionLabel = useMemo(
    () => (planos.length > 0 ? ` (${planos.length} selecionado${planos.length > 1 ? 's' : ''})` : ''),
    [planos.length],
  );
  const executoresSetoresSelectionLabel = useMemo(() => {
    const total = (executores?.length ?? 0) + (setores?.length ?? 0);
    return total > 0 ? ` (${total} selecionado${total > 1 ? 's' : ''})` : '';
  }, [executores?.length, setores?.length]);
  const ambientesEquipamentosSelectionLabel = useMemo(() => {
    const total = areaGroups.length + areaSubgroups.length + areas.length + equipmentTypes.length + equipment.length;
    return total > 0 ? ` (${total} selecionado${total > 1 ? 's' : ''})` : '';
  }, [areaGroups.length, areaSubgroups.length, areas.length, equipmentTypes.length, equipment.length]);
  const planosValues = useMemo(() => planos.map(opt => opt.value), [planos]);
  const executoresValues = useMemo(() => (executores || []).map(opt => opt.value), [executores]);
  const tempExecutoresValues = useMemo(() => tempExecutores.map(opt => opt.value), [tempExecutores]);
  const setoresValues = useMemo(() => (setores || []).map(opt => opt.value), [setores]);
  const tempSetoresValues = useMemo(() => tempSetores.map(opt => opt.value), [tempSetores]);
  const maintenanceUnitsValues = useMemo(() => maintenanceUnits.map(opt => opt.value), [maintenanceUnits]);
  const tempPlanosValues = useMemo(() => tempPlanos.map(opt => opt.value), [tempPlanos]);
  const areaGroupsValues = useMemo(() => areaGroups.map(opt => opt.value), [areaGroups]);
  const tempAreaGroupsValues = useMemo(() => tempAreaGroups.map(opt => opt.value), [tempAreaGroups]);
  const areaSubgroupsValues = useMemo(() => areaSubgroups.map(opt => opt.value), [areaSubgroups]);
  const tempAreaSubgroupsValues = useMemo(() => tempAreaSubgroups.map(opt => opt.value), [tempAreaSubgroups]);
  const areasValues = useMemo(() => areas.map(opt => opt.value), [areas]);
  const tempAreasValues = useMemo(() => tempAreas.map(opt => opt.value), [tempAreas]);
  const equipmentTypesValues = useMemo(() => equipmentTypes.map(opt => opt.value), [equipmentTypes]);
  const tempEquipmentTypesValues = useMemo(() => tempEquipmentTypes.map(opt => opt.value), [tempEquipmentTypes]);
  const equipmentValues = useMemo(() => equipment.map(opt => opt.value), [equipment]);
  const tempEquipmentValues = useMemo(() => tempEquipment.map(opt => opt.value), [tempEquipment]);
  const allMaintenanceUnitsValues = useMemo(() => {
    const localValues = maintenanceUnitsValues;
    const globalUnitId = headerFilters?.units?.id ? String(headerFilters.units.id) : null;
    if (localValues.length === 0 && globalUnitId) {
      return [globalUnitId];
    }
    if (globalUnitId && !localValues.includes(globalUnitId)) {
      return [...localValues, globalUnitId];
    }
    return localValues;
  }, [maintenanceUnitsValues, headerFilters?.units?.id]);
  const globalUnitGroupValues = useMemo(() => {
    const globalUnitGroupId = headerFilters?.unitGroups?.id ? String(headerFilters.unitGroups.id) : null;
    return globalUnitGroupId ? [globalUnitGroupId] : [];
  }, [headerFilters?.unitGroups?.id]);
  const globalUnitSubgroupValues = useMemo(() => {
    const globalUnitSubgroupId = headerFilters?.unitSubgroups?.id ? String(headerFilters.unitSubgroups.id) : null;
    return globalUnitSubgroupId ? [globalUnitSubgroupId] : [];
  }, [headerFilters?.unitSubgroups?.id]);
  const globalStateValues = useMemo(() => {
    const globalStateId = headerFilters?.states?.id ? String(headerFilters.states.id) : null;
    return globalStateId ? [globalStateId] : [];
  }, [headerFilters?.states?.id]);
  const globalCityValues = useMemo(() => {
    const globalCityId = headerFilters?.cities?.id ? String(headerFilters.cities.id) : null;
    return globalCityId ? [globalCityId] : [];
  }, [headerFilters?.cities?.id]);

  const globalSystemValues = useMemo(() => {
    const globalSystemId = headerFilters?.systems?.id ? String(headerFilters.systems.id) : null;
    return globalSystemId ? [globalSystemId] : [];
  }, [headerFilters?.systems?.id]);

  const setExecutores = useCallback((executores: FilterOption[] | undefined) => {
    setExecutoresState(executores || []);
  }, []);

  const setSetores = useCallback((setores: FilterOption[] | undefined) => {
    setSetoresState(setores || []);
  }, []);

  const handlePlanosChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = planosOptions.filter(opt => stringValues.includes(opt.value));
      setPlanos(selectedOptions);
    },
    [planosOptions],
  );

  const handleExecutoresChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = executoresOptions.filter(opt => stringValues.includes(opt.value));
      setExecutores(selectedOptions.length > 0 ? selectedOptions : undefined);
    },
    [executoresOptions, setExecutores],
  );

  const handleTempExecutoresChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = executoresOptions.filter(opt => stringValues.includes(opt.value));
      setTempExecutores(selectedOptions);
    },
    [executoresOptions],
  );

  const handleSetoresChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = setoresOptions.filter(opt => stringValues.includes(opt.value));
      setSetores(selectedOptions.length > 0 ? selectedOptions : undefined);
    },
    [setoresOptions, setSetores],
  );

  const handleTempSetoresChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = setoresOptions.filter(opt => stringValues.includes(opt.value));
      setTempSetores(selectedOptions);
    },
    [setoresOptions],
  );

  const applyExecutoresSetoresFilters = useCallback(() => {
    setExecutores(tempExecutores.length > 0 ? tempExecutores : undefined);
    setSetores(tempSetores.length > 0 ? tempSetores : undefined);
    setExecutoresSetoresModalOpen(false);
  }, [tempExecutores, tempSetores]);

  const clearExecutoresSetoresFilters = useCallback(() => {
    setTempExecutores([]);
    setTempSetores([]);
    setExecutores([]);
    setSetores([]);
    setExecutoresSetoresModalOpen(false);
  }, []);

  const handleTempPlanosChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = planosOptions.filter(opt => stringValues.includes(opt.value));
      setTempPlanos(selectedOptions);
    },
    [planosOptions],
  );

  const applyPlanosFilters = useCallback(() => {
    setPlanos(tempPlanos);
    setPlanosModalOpen(false);
  }, [tempPlanos]);

  const clearPlanosFilters = useCallback(() => {
    setTempPlanos([]);
    setPlanos([]);
    setPlanosModalOpen(false);
  }, []);

  const handleMaintenanceUnitsChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = maintenanceUnitsOptions.filter(opt => stringValues.includes(opt.value));
      setMaintenanceUnits(selectedOptions);
    },
    [maintenanceUnitsOptions],
  );

  const handleAreaGroupsChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = areaGroupsOptions.filter(opt => stringValues.includes(opt.value));
      setAreaGroups(selectedOptions);
    },
    [areaGroupsOptions],
  );

  const handleAreaSubgroupsChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = areaSubgroupsOptions.filter(opt => stringValues.includes(opt.value));
      setAreaSubgroups(selectedOptions);
    },
    [areaSubgroupsOptions],
  );

  const handleAreasChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = areasOptions.filter(opt => stringValues.includes(opt.value));
      setAreas(selectedOptions);
    },
    [areasOptions],
  );

  const handleEquipmentTypesChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = equipmentTypesOptions.filter(opt => stringValues.includes(opt.value));
      setEquipmentTypes(selectedOptions);
    },
    [equipmentTypesOptions],
  );

  const handleEquipmentChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = equipmentOptions.filter(opt => stringValues.includes(opt.value));
      setEquipment(selectedOptions);
    },
    [equipmentOptions],
  );

  const handleTempAreaGroupsChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = areaGroupsOptions.filter(opt => stringValues.includes(opt.value));
      setTempAreaGroups(selectedOptions);
    },
    [areaGroupsOptions],
  );

  const handleTempAreaSubgroupsChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = areaSubgroupsOptions.filter(opt => stringValues.includes(opt.value));
      setTempAreaSubgroups(selectedOptions);
    },
    [areaSubgroupsOptions],
  );

  const handleTempAreasChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = areasOptions.filter(opt => stringValues.includes(opt.value));
      setTempAreas(selectedOptions);
    },
    [areasOptions],
  );

  const handleTempEquipmentTypesChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = equipmentTypesOptions.filter(opt => stringValues.includes(opt.value));
      setTempEquipmentTypes(selectedOptions);
    },
    [equipmentTypesOptions],
  );

  const handleTempEquipmentChange = useCallback(
    (values: unknown) => {
      const stringValues = (values as string[]) || [];
      const selectedOptions = equipmentOptions.filter(opt => stringValues.includes(opt.value));
      setTempEquipment(selectedOptions);
    },
    [equipmentOptions],
  );

  const applyAmbientesEquipamentosFilters = useCallback(() => {
    setAreaGroups(tempAreaGroups);
    setAreaSubgroups(tempAreaSubgroups);
    setAreas(tempAreas);
    setEquipmentTypes(tempEquipmentTypes);
    setEquipment(tempEquipment);
    setAmbientesEquipamentosModalOpen(false);
  }, [tempAreaGroups, tempAreaSubgroups, tempAreas, tempEquipmentTypes, tempEquipment]);

  const clearAmbientesEquipamentosFilters = useCallback(() => {
    setTempAreaGroups([]);
    setTempAreaSubgroups([]);
    setTempAreas([]);
    setTempEquipmentTypes([]);
    setTempEquipment([]);
    setAreaGroups([]);
    setAreaSubgroups([]);
    setAreas([]);
    setEquipmentTypes([]);
    setEquipment([]);
    setAmbientesEquipamentosModalOpen(false);
  }, []);

  const clearAllFilters = useCallback(() => {
    setPlanos([]);
    setOcorrencias([]);
    setExecutoresState([]);
    setSetoresState([]);
    setMaintenanceUnits([]);
    setAreaGroups([]);
    setAreaSubgroups([]);
    setAreas([]);
    setEquipmentTypes([]);
    setEquipment([]);
    setType('pendentes');
  }, []);

  const fetchActivitiesPlans = useCallback(async () => {
    const companyId = headerFilters?.companies?.id;
    const systemId = headerFilters?.systems?.id;

    const unidadeIds =
      maintenanceUnits.length > 0
        ? maintenanceUnits.map(mu => Number(mu.value)).filter(n => !Number.isNaN(n))
        : headerFilters?.units?.id != null
        ? [Number(headerFilters.units.id)]
        : undefined;

    if (!companyId) {
      setPlanosOptions([]);
      return;
    }

    const companyIdNum = Number(companyId);
    const systemIdNum = systemId ? Number(systemId) : null;
    const systemIds = systemIdNum ? [systemIdNum] : undefined;

    const grupoUnidadeId = headerFilters?.unitGroups?.id;
    const subGrupoUnidadeId = headerFilters?.unitSubgroups?.id;
    const estadoId = headerFilters?.states?.id;
    const cidadeId = headerFilters?.cities?.id;
    const fornecedorId = headerFilters?.providers?.id;

    try {
      Sentry.logger.info('Buscando planos de atividades para os filtros da agenda', empresaAttrs);
      const response = await AgendaService.getActivitiesPlans({
        companyId: companyIdNum,
        ...(unidadeIds?.length && { unidadeIds }),
        systemIds,
        ...(grupoUnidadeId != null && { grupoUnidadeIds: [Number(grupoUnidadeId)] }),
        ...(subGrupoUnidadeId != null && { subGrupoUnidadeIds: [Number(subGrupoUnidadeId)] }),
        ...(estadoId != null && { estadoIds: [Number(estadoId)] }),
        ...(cidadeId != null && { cidadeIds: [Number(cidadeId)] }),
        ...(fornecedorId != null && { fornecedorIds: [Number(fornecedorId)] }),
      });

      if (!response?.data) {
        setPlanosOptions([]);
        return;
      }

      const options: FilterOption[] = (response.data as ActivityPlan[]).map(item => ({
        label: item.nome,
        value: String(item.planoId),
      }));

      setPlanosOptions(options);
    } catch (error) {
      console.error('Erro ao buscar planos de atividades:', error);
      Sentry.captureException(error, {
        tags: { page: 'agenda', action: 'fetch_planos', empresa: headerFilters?.companies?.id },
      });
      setPlanosOptions([]);
    }
  }, [
    headerFilters?.companies?.id,
    headerFilters?.units?.id,
    headerFilters?.systems?.id,
    headerFilters?.unitGroups?.id,
    headerFilters?.unitSubgroups?.id,
    headerFilters?.states?.id,
    headerFilters?.cities?.id,
    headerFilters?.providers?.id,
    planosModalOpen,
    maintenanceUnits,
  ]);

  const fetchSetores = useCallback(
    async (unitIdOverride?: string[]) => {
      const companyId = headerFilters?.companies?.id;

      if (!companyId) {
        setSetoresOptions([]);
        return;
      }

      const unitIdValues = unitIdOverride ?? maintenanceUnitsValues.map(v => v).filter(Boolean);
      const unidadeIds =
        unitIdValues.length > 0
          ? unitIdValues.map(id => Number(id)).filter(n => !Number.isNaN(n))
          : headerFilters?.units?.id != null
          ? [Number(headerFilters.units.id)]
          : undefined;

      const grupoUnidadeId = headerFilters?.unitGroups?.id;
      const subGrupoUnidadeId = headerFilters?.unitSubgroups?.id;
      const estadoId = headerFilters?.states?.id;
      const cidadeId = headerFilters?.cities?.id;
      const fornecedorId = headerFilters?.providers?.id;

      const params: Parameters<typeof AgendaService.getCompanyDepartments>[0] = {
        companyId: Number(companyId),
        ...(unidadeIds?.length && { unidadeIds }),
        ...(grupoUnidadeId != null && { grupoUnidadeIds: [Number(grupoUnidadeId)] }),
        ...(subGrupoUnidadeId != null && { subGrupoUnidadeIds: [Number(subGrupoUnidadeId)] }),
        ...(estadoId != null && { estadoIds: [Number(estadoId)] }),
        ...(cidadeId != null && { cidadeIds: [Number(cidadeId)] }),
        ...(fornecedorId != null && { fornecedorIds: [Number(fornecedorId)] }),
      };

      try {
        Sentry.logger.info('Buscando setores para o filtro Executores e Setores da agenda', empresaAttrs);
        const response = await AgendaService.getCompanyDepartments(params);

        if (!response?.data) {
          setSetoresOptions([]);
          return;
        }

        const options: FilterOption[] = (response.data as Department[]).map(item => ({
          label: item.nome,
          value: String(item.setor),
        }));

        setSetoresOptions(options);
      } catch (error) {
        console.error('Erro ao buscar setores:', error);
        Sentry.captureException(error, {
          tags: { page: 'agenda', action: 'fetch_setores', empresa: headerFilters?.companies?.id },
        });
        setSetoresOptions([]);
      }
    },
    [
      headerFilters?.companies?.id,
      headerFilters?.units?.id,
      headerFilters?.unitGroups?.id,
      headerFilters?.unitSubgroups?.id,
      headerFilters?.states?.id,
      headerFilters?.cities?.id,
      headerFilters?.providers?.id,
      maintenanceUnitsValues,
    ],
  );

  const mapUsersToOptions = useCallback((users: User[]): FilterOption[] => {
    return users.map(item => {
      let fotoUrl: string | undefined;
      if (item.image) {
        if (item.image.startsWith('http://') || item.image.startsWith('https://')) {
          fotoUrl = item.image;
        } else {
          const baseUrl = process.env.REACT_APP_URL_IMAGES || '';
          const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
          const cleanPath = item.image.startsWith('/') ? item.image : `/${item.image}`;
          const url = `${cleanBaseUrl}${cleanPath}`;
          if (url && url !== '/') {
            fotoUrl = url;
          }
        }
      }
      return {
        label: item.nome,
        value: String(item.usuario),
        foto: fotoUrl ?? '',
      };
    });
  }, []);

  const fetchExecutoresBySearch = useCallback(
    async (search: string) => {
      if (!search.trim()) {
        setExecutoresOptions(tempExecutores);
        return;
      }

      const companyId = headerFilters?.companies?.id;

      if (!companyId) {
        setExecutoresOptions(tempExecutores);
        return;
      }

      const unidadeId =
        maintenanceUnitsValues.length > 0
          ? maintenanceUnitsValues.map(id => Number(id)).filter(n => !Number.isNaN(n))
          : headerFilters?.units?.id != null
          ? [Number(headerFilters.units.id)]
          : undefined;

      const grupoUnidadeId = headerFilters?.unitGroups?.id;
      const subGrupoUnidadeId = headerFilters?.unitSubgroups?.id;
      const estadoId = headerFilters?.states?.id;
      const cidadeId = headerFilters?.cities?.id;
      const fornecedorId = headerFilters?.providers?.id;

      const params: Parameters<typeof AgendaService.getUsers>[0] = {
        companyId: Number(companyId),
        ...(unidadeId?.length && { unidadeId }),
        ...(grupoUnidadeId != null && { grupoUnidadeId: [Number(grupoUnidadeId)] }),
        ...(subGrupoUnidadeId != null && { subGrupoUnidadeId: [Number(subGrupoUnidadeId)] }),
        ...(estadoId != null && { estadoId: [Number(estadoId)] }),
        ...(cidadeId != null && { cidadeId: [Number(cidadeId)] }),
        ...(fornecedorId != null && { fornecedorId: [Number(fornecedorId)] }),
        ...(search.trim() && { search: search.trim() }),
      };

      try {
        Sentry.logger.info('Buscando executores para o filtro da agenda', empresaAttrs);
        const response = await AgendaService.getUsers(params);

        if (!response?.data) {
          setExecutoresOptions(tempExecutores);
          return;
        }

        const options: FilterOption[] = mapUsersToOptions(response.data as User[]);
        setExecutoresOptions(() => {
          const byValue = new Map<string, FilterOption>();
          tempExecutores.forEach(t => byValue.set(t.value, t));
          options.forEach(o => byValue.set(o.value, o));
          return Array.from(byValue.values());
        });
      } catch (error) {
        console.error('Erro ao buscar executores:', error);
        Sentry.captureException(error, {
          tags: { page: 'agenda', action: 'fetch_executores', empresa: headerFilters?.companies?.id },
        });
        setExecutoresOptions(tempExecutores);
      }
    },
    [
      headerFilters?.companies?.id,
      headerFilters?.units?.id,
      headerFilters?.unitGroups?.id,
      headerFilters?.unitSubgroups?.id,
      headerFilters?.states?.id,
      headerFilters?.cities?.id,
      headerFilters?.providers?.id,
      maintenanceUnitsValues,
      tempExecutores,
      mapUsersToOptions,
    ],
  );

  const onExecutoresSearch = useCallback(
    (term: string) => {
      if (debounceExecutoresSearchRef.current) {
        clearTimeout(debounceExecutoresSearchRef.current);
      }
      debounceExecutoresSearchRef.current = setTimeout(() => {
        fetchExecutoresBySearch(term);
      }, 400);
    },
    [fetchExecutoresBySearch],
  );

  const fetchMaintenanceUnits = useCallback(async () => {
    const companyId = headerFilters?.companies?.id;
    const unitId = headerFilters?.units?.id;

    if (!companyId) {
      setMaintenanceUnitsOptions([]);
      return;
    }

    try {
      Sentry.logger.info('Buscando unidades de manutenção para a agenda', empresaAttrs);
      const grupoUnidadeId = headerFilters?.unitGroups?.id;
      const subGrupoUnidadeId = headerFilters?.unitSubgroups?.id;
      const estadoId = headerFilters?.states?.id;
      const cidadeId = headerFilters?.cities?.id;
      const fornecedorId = headerFilters?.providers?.id;

      const response = await AgendaService.getMaintenanceUnits({
        companyId: Number(companyId),
        unitId: unitId ? Number(unitId) : undefined,
        ...(grupoUnidadeId != null && { grupoUnidadeIds: [Number(grupoUnidadeId)] }),
        ...(subGrupoUnidadeId != null && { subGrupoUnidadeIds: [Number(subGrupoUnidadeId)] }),
        ...(estadoId != null && { estadoId: Number(estadoId) }),
        ...(cidadeId != null && { cidadeId: Number(cidadeId) }),
        ...(fornecedorId != null && { fornecedorId: Number(fornecedorId) }),
      });

      if (!response?.data) {
        setMaintenanceUnitsOptions([]);
        return;
      }

      const options: FilterOption[] = (response.data as MaintenanceUnit[]).map(item => ({
        label: item.nome,
        value: String(item.site),
      }));

      setMaintenanceUnitsOptions(options);

      // Pré-seleciona se houver filtro global de units
      const globalUnitId = headerFilters?.units?.id;
      if (globalUnitId) {
        const globalUnitOption = options.find(opt => opt.value === String(globalUnitId));
        if (globalUnitOption && !maintenanceUnits.find(opt => opt.value === String(globalUnitId))) {
          setMaintenanceUnits([globalUnitOption]);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar unidades de manutenção:', error);
      Sentry.captureException(error, {
        tags: { page: 'agenda', action: 'fetch_unidades', empresa: headerFilters?.companies?.id },
      });
      setMaintenanceUnitsOptions([]);
    }
  }, [
    headerFilters?.companies?.id,
    headerFilters?.units?.id,
    headerFilters?.unitGroups?.id,
    headerFilters?.unitSubgroups?.id,
    headerFilters?.states?.id,
    headerFilters?.cities?.id,
    headerFilters?.providers?.id,
    maintenanceUnits,
  ]);

  const fetchAreaGroups = useCallback(async () => {
    const companyId = headerFilters?.companies?.id;
    const unitId = headerFilters?.units?.id;

    if (!companyId) {
      setAreaGroupsOptions([]);
      return;
    }

    const unidadeId =
      maintenanceUnits.length > 0
        ? maintenanceUnits.map(mu => Number(mu.value)).filter(n => !Number.isNaN(n))
        : unitId != null
        ? [Number(unitId)]
        : undefined;
    const grupoUnidadeId = headerFilters?.unitGroups?.id;
    const subGrupoUnidadeId = headerFilters?.unitSubgroups?.id;
    const estadoId = headerFilters?.states?.id;
    const cidadeId = headerFilters?.cities?.id;
    const fornecedorId = headerFilters?.providers?.id;

    const params: Parameters<typeof AgendaService.getAreaGroups>[0] = {
      companyId: Number(companyId),
      ...(unidadeId?.length && { unidadeId }),
      ...(grupoUnidadeId != null && { grupoUnidadeId: [Number(grupoUnidadeId)] }),
      ...(subGrupoUnidadeId != null && { subGrupoUnidadeId: [Number(subGrupoUnidadeId)] }),
      ...(estadoId != null && { estadoId: [Number(estadoId)] }),
      ...(cidadeId != null && { cidadeId: [Number(cidadeId)] }),
      ...(fornecedorId != null && { fornecedorId: [Number(fornecedorId)] }),
    };

    try {
      Sentry.logger.info('Buscando grupos de área para o filtro Ambientes e Equipamentos da agenda', empresaAttrs);
      const response = await AgendaService.getAreaGroups(params);

      if (!response?.data) {
        setAreaGroupsOptions([]);
        return;
      }

      const options: FilterOption[] = (response.data as AreaGroup[]).map(item => ({
        label: item.nome,
        value: String(item.grupoArea),
      }));

      setAreaGroupsOptions(options);
    } catch (error) {
      console.error('Erro ao buscar grupos de áreas:', error);
      Sentry.captureException(error, {
        tags: { page: 'agenda', action: 'fetch_grupos_area', empresa: headerFilters?.companies?.id },
      });
      setAreaGroupsOptions([]);
    }
  }, [
    headerFilters?.companies?.id,
    headerFilters?.units?.id,
    headerFilters?.unitGroups?.id,
    headerFilters?.unitSubgroups?.id,
    headerFilters?.states?.id,
    headerFilters?.cities?.id,
    headerFilters?.providers?.id,
    maintenanceUnits,
  ]);

  const fetchAreaSubgroups = useCallback(async () => {
    const companyId = headerFilters?.companies?.id;
    const unitId = headerFilters?.units?.id;

    if (!companyId) {
      setAreaSubgroupsOptions([]);
      return;
    }

    const unidadeId =
      maintenanceUnits.length > 0
        ? maintenanceUnits.map(mu => Number(mu.value)).filter(n => !Number.isNaN(n))
        : unitId != null
        ? [Number(unitId)]
        : undefined;
    const grupoAreaId = tempAreaGroups.length > 0 ? tempAreaGroups.map(ag => ag.value) : undefined;
    const grupoUnidadeId = headerFilters?.unitGroups?.id;
    const subGrupoUnidadeId = headerFilters?.unitSubgroups?.id;
    const estadoId = headerFilters?.states?.id;
    const cidadeId = headerFilters?.cities?.id;
    const fornecedorId = headerFilters?.providers?.id;

    const params: Parameters<typeof AgendaService.getAreaSubgroups>[0] = {
      companyId: Number(companyId),
      ...(unidadeId?.length && { unidadeId }),
      ...(grupoAreaId?.length && { grupoAreaId }),
      ...(grupoUnidadeId != null && { grupoUnidadeId: [Number(grupoUnidadeId)] }),
      ...(subGrupoUnidadeId != null && { subGrupoUnidadeId: [Number(subGrupoUnidadeId)] }),
      ...(estadoId != null && { estadoId: [Number(estadoId)] }),
      ...(cidadeId != null && { cidadeId: [Number(cidadeId)] }),
      ...(fornecedorId != null && { fornecedorId: [Number(fornecedorId)] }),
    };

    try {
      Sentry.logger.info('Buscando subgrupos de área para a agenda', empresaAttrs);
      const response = await AgendaService.getAreaSubgroups(params);

      if (!response?.data) {
        setAreaSubgroupsOptions([]);
        return;
      }

      const options: FilterOption[] = (response.data as AreaSubgroup[]).map(item => ({
        label: item.nome,
        value: String(item.subGrupoArea),
      }));

      setAreaSubgroupsOptions(options);
    } catch (error) {
      console.error('Erro ao buscar subgrupos de áreas:', error);
      Sentry.captureException(error, {
        tags: { page: 'agenda', action: 'fetch_subgrupos_area', empresa: headerFilters?.companies?.id },
      });
      setAreaSubgroupsOptions([]);
    }
  }, [
    headerFilters?.companies?.id,
    headerFilters?.units?.id,
    headerFilters?.unitGroups?.id,
    headerFilters?.unitSubgroups?.id,
    headerFilters?.states?.id,
    headerFilters?.cities?.id,
    headerFilters?.providers?.id,
    areaGroups,
    tempAreaGroups,
  ]);

  const fetchAreas = useCallback(
    async (page: number = 1, search: string = '', append: boolean = false) => {
      const companyId = headerFilters?.companies?.id;

      if (!companyId) {
        if (!append) setAreasOptions([]);
        return;
      }

      try {
        Sentry.logger.info('Buscando áreas para o filtro da agenda', empresaAttrs);
        const groupsToUse = ambientesEquipamentosModalOpen ? tempAreaGroups : areaGroups;
        const subgroupsToUse = ambientesEquipamentosModalOpen ? tempAreaSubgroups : areaSubgroups;

        const siteIdArray = maintenanceUnits.length > 0 ? maintenanceUnits.map(mu => Number(mu.value)) : undefined;
        const grupoAreaIdArray = groupsToUse.length > 0 ? groupsToUse.map(ag => ag.value) : undefined;
        const subGrupoAreaIdArray = subgroupsToUse.length > 0 ? subgroupsToUse.map(asg => asg.value) : undefined;

        const response = await AgendaService.getAreas({
          companyId: Number(companyId),
          siteId: siteIdArray,
          grupoAreaId: grupoAreaIdArray,
          subGrupoAreaId: subGrupoAreaIdArray,
          search: search || undefined,
          pageIndex: page,
          pageSize: 50,
        });

        if (!response?.data) {
          if (!append) setAreasOptions([]);
          return;
        }

        const options: FilterOption[] = (response.data as Area[]).map(item => ({
          label: item.identificacaoAmbiente ? `${item.nome} - ${item.identificacaoAmbiente}` : item.nome,
          value: String(item.area),
        }));

        if (append) {
          setAreasOptions(prev => [...prev, ...options]);
        } else {
          setAreasOptions(options);
        }
      } catch (error) {
        console.error('Erro ao buscar áreas:', error);
        Sentry.captureException(error, {
          tags: { page: 'agenda', action: 'fetch_areas', empresa: headerFilters?.companies?.id },
        });
        if (!append) setAreasOptions([]);
      }
    },
    [
      headerFilters?.companies?.id,
      ambientesEquipamentosModalOpen,
      maintenanceUnits,
      areaGroups,
      tempAreaGroups,
      areaSubgroups,
      tempAreaSubgroups,
    ],
  );

  const loadMoreAreas = useCallback(async () => {
    const nextPage = areaPage + 1;
    await fetchAreas(nextPage, areaSearch, true);
    setAreaPage(nextPage);
  }, [areaPage, areaSearch, fetchAreas]);

  const searchArea = useCallback(
    (term: string) => {
      setAreaSearch(term);
      setAreaPage(1);

      if (debounceAreaRef.current) {
        clearTimeout(debounceAreaRef.current);
      }

      debounceAreaRef.current = setTimeout(async () => {
        await fetchAreas(1, term, false);
      }, 400);
    },
    [fetchAreas],
  );

  const fetchEquipmentTypes = useCallback(async () => {
    const companyId = headerFilters?.companies?.id;
    const unitId = headerFilters?.units?.id;

    if (!companyId) {
      setEquipmentTypesOptions([]);
      return;
    }

    try {
      Sentry.logger.info('Buscando tipos de equipamento para a agenda', empresaAttrs);
      const response = await AgendaService.getEquipmentTypes({
        companyId: Number(companyId),
        unitId: Number(unitId),
      });

      if (!response?.data) {
        setEquipmentTypesOptions([]);
        return;
      }

      const filteredData = (response.data as EquipmentType[]).filter(item => !item.inativo);
      equipmentTypesDataRef.current = filteredData;

      const options: FilterOption[] = filteredData.map(item => ({
        label: item.nome,
        value: String(item.linhaProdutoEmpresa),
      }));

      setEquipmentTypesOptions(options);
    } catch (error) {
      console.error('Erro ao buscar tipos de equipamentos:', error);
      Sentry.captureException(error, {
        tags: { page: 'agenda', action: 'fetch_tipos_equipamento', empresa: headerFilters?.companies?.id },
      });
      setEquipmentTypesOptions([]);
    }
  }, [headerFilters?.companies?.id, headerFilters?.units?.id]);

  const fetchEquipments = useCallback(
    async (page: number = 1, search: string = '', append: boolean = false) => {
      const companyId = headerFilters?.companies?.id;

      if (!companyId) {
        if (!append) setEquipmentOptions([]);
        return;
      }

      try {
        const groupsToUse = ambientesEquipamentosModalOpen ? tempAreaGroups : areaGroups;
        const subgroupsToUse = ambientesEquipamentosModalOpen ? tempAreaSubgroups : areaSubgroups;
        const areasToUse = ambientesEquipamentosModalOpen ? tempAreas : areas;
        const equipmentTypesToUse = ambientesEquipamentosModalOpen ? tempEquipmentTypes : equipmentTypes;

        const siteIdArray = maintenanceUnits.length > 0 ? maintenanceUnits.map(mu => Number(mu.value)) : undefined;
        const grupoAreaIdArray = groupsToUse.length > 0 ? groupsToUse.map(ag => Number(ag.value)) : undefined;
        const subGrupoAreaIdArray =
          subgroupsToUse.length > 0 ? subgroupsToUse.map(asg => Number(asg.value)) : undefined;
        const areaIdArray = areasToUse.length > 0 ? areasToUse.map(a => a.value) : undefined;
        const tipoEquipamentoIdRaw = equipmentTypesToUse.length > 0 ? Number(equipmentTypesToUse[0].value) : undefined;
        const tipoEquipamentoId =
          tipoEquipamentoIdRaw !== undefined && !Number.isNaN(tipoEquipamentoIdRaw) ? tipoEquipamentoIdRaw : undefined;

        Sentry.logger.info('Buscando equipamentos para o filtro da agenda', empresaAttrs);
        const response = await AgendaService.getEquipments({
          companyId: Number(companyId),
          siteId: siteIdArray,
          grupoAreaId: grupoAreaIdArray,
          subGrupoAreaId: subGrupoAreaIdArray,
          areaId: areaIdArray,
          tipoEquipamentoId: tipoEquipamentoId,
          search: search || undefined,
          pageIndex: page,
          pageSize: 50,
        });

        if (!response?.data) {
          if (!append) setEquipmentOptions([]);
          return;
        }

        const options: FilterOption[] = (response.data as Equipment[]).map(item => ({
          label: item.tag ? `${item.nome} - ${item.tag}` : item.nome,
          value: String(item.equipamento),
        }));

        if (append) {
          setEquipmentOptions(prev => [...prev, ...options]);
        } else {
          setEquipmentOptions(options);
        }
      } catch (error) {
        console.error('Erro ao buscar equipamentos:', error);
        Sentry.captureException(error, {
          tags: { page: 'agenda', action: 'fetch_equipamentos', empresa: headerFilters?.companies?.id },
        });
        if (!append) setEquipmentOptions([]);
      }
    },
    [
      headerFilters?.companies?.id,
      ambientesEquipamentosModalOpen,
      maintenanceUnits,
      areaGroups,
      tempAreaGroups,
      areaSubgroups,
      tempAreaSubgroups,
      areas,
      tempAreas,
      equipmentTypes,
      tempEquipmentTypes,
    ],
  );

  const loadMoreEquipamentos = useCallback(async () => {
    const nextPage = equipamentoPage + 1;
    await fetchEquipments(nextPage, equipamentoSearch, true);
    setEquipamentoPage(nextPage);
  }, [equipamentoPage, equipamentoSearch, fetchEquipments]);

  const searchEquipamento = useCallback(
    (term: string) => {
      setEquipamentoSearch(term);
      setEquipamentoPage(1);

      if (debounceEquipamentoRef.current) {
        clearTimeout(debounceEquipamentoRef.current);
      }

      debounceEquipamentoRef.current = setTimeout(async () => {
        await fetchEquipments(1, term, false);
      }, 400);
    },
    [fetchEquipments],
  );

  // API de setores/executores depende das Unidades selecionadas na tela
  const maintenanceUnitsValuesKey = useMemo(
    () => [...maintenanceUnitsValues].sort().join(','),
    [maintenanceUnitsValues],
  );
  useEffect(() => {
    if (!executoresSetoresModalOpen) {
      return;
    }
    fetchSetores(maintenanceUnitsValues);
    setExecutoresOptions(executores || []);
  }, [
    executoresSetoresModalOpen,
    maintenanceUnitsValuesKey,
    headerFilters?.companies?.id,
    headerFilters?.units?.id,
    headerFilters?.unitGroups?.id,
    headerFilters?.unitSubgroups?.id,
    headerFilters?.states?.id,
    headerFilters?.cities?.id,
    headerFilters?.providers?.id,
  ]);

  useEffect(() => {
    if (!ambientesEquipamentosModalOpen) {
      return;
    }

    fetchAreaGroups();
  }, [
    ambientesEquipamentosModalOpen,
    headerFilters?.companies?.id,
    headerFilters?.units?.id,
    headerFilters?.unitGroups?.id,
    headerFilters?.unitSubgroups?.id,
    headerFilters?.states?.id,
    headerFilters?.cities?.id,
    headerFilters?.providers?.id,
    maintenanceUnits,
  ]);

  useEffect(() => {
    if (!ambientesEquipamentosModalOpen) {
      return;
    }

    fetchAreaSubgroups();
  }, [
    ambientesEquipamentosModalOpen,
    headerFilters?.companies?.id,
    headerFilters?.units?.id,
    headerFilters?.unitGroups?.id,
    headerFilters?.unitSubgroups?.id,
    headerFilters?.states?.id,
    headerFilters?.cities?.id,
    headerFilters?.providers?.id,
    maintenanceUnits,
    areaGroups,
    tempAreaGroups,
  ]);

  useEffect(() => {
    if (!ambientesEquipamentosModalOpen) {
      return;
    }

    fetchAreas();
  }, [ambientesEquipamentosModalOpen, headerFilters?.companies?.id, maintenanceUnits, areaGroups, areaSubgroups]);

  useEffect(() => {
    if (!ambientesEquipamentosModalOpen) {
      return;
    }

    fetchEquipmentTypes();
  }, [ambientesEquipamentosModalOpen, headerFilters?.companies?.id, headerFilters?.units?.id]);

  useEffect(() => {
    if (!ambientesEquipamentosModalOpen) {
      return;
    }

    setEquipamentoPage(1);
    setEquipamentoSearch('');
    fetchEquipments(1, '', false);
  }, [
    ambientesEquipamentosModalOpen,
    headerFilters?.companies?.id,
    maintenanceUnits,
    areaGroups,
    areaSubgroups,
    areas,
    equipmentTypes,
    fetchEquipments,
  ]);

  const setWeekView = useCallback((isWeekView: boolean) => {
    isWeekViewRef.current = isWeekView;
  }, []);

  const fetchAgendaTasks = useCallback(
    async (isWeekView?: boolean, overrides?: { planosValues?: string[] }) => {
      const companyId = headerFilters?.companies?.id;

      if (!companyId || !effectiveDate) {
        if (isMountedRef.current) setEvents({});
        return;
      }

      if (isMountedRef.current) setIsLoadingEvents(true);

      try {
        const baixadas = type === 'baixadas';

        const isWeek =
          isWeekView !== undefined
            ? isWeekView
            : view !== undefined
            ? view === CalendarView.Semana
            : isWeekViewRef.current;
        const dateToUse = effectiveDate;
        const selectedDate = dateToUse.toISOString();

        const shouldAddDays = isWeek;

        const baseParams: Parameters<typeof AgendaService.getAgendaTasks>[0] = {
          SelectedDate: selectedDate,
          Baixadas: baixadas,
          EmpresaId: Number(companyId),
          ...(headerFilters?.providers?.id && { FornecedorId: Number(headerFilters.providers.id) }),
          ...(shouldAddDays && { DiasAdicionais: 6 }),
        };

        const effectivePlanosValues = overrides?.planosValues ?? planosValues;

        const apiParams = buildApiPayload(baseParams, {
          unitGroupValues: {
            values: globalUnitGroupValues,
            apiFieldName: 'GrupoUnidadeId',
            alwaysArray: false,
          },
          unitSubgroupValues: {
            values: globalUnitSubgroupValues,
            apiFieldName: 'SubGrupoUnidadeId',
            alwaysArray: false,
          },
          stateValues: {
            values: globalStateValues,
            apiFieldName: 'EstadoId',
            alwaysArray: false,
          },
          cityValues: {
            values: globalCityValues,
            apiFieldName: 'CidadeId',
            alwaysArray: false,
          },
          systemValues: {
            values: globalSystemValues,
            apiFieldName: 'SistemaEmpresaId',
            alwaysArray: false,
          },
          maintenanceUnitsValues: {
            values: allMaintenanceUnitsValues,
            apiFieldName: 'SiteId',
            alwaysArray: true,
          },
          planosValues: {
            values: effectivePlanosValues,
            apiFieldName: 'PlanoAtividadeId',
            alwaysArray: false,
          },
          executoresValues: {
            values: executoresValues,
            apiFieldName: 'ExecutorId',
            alwaysArray: false,
          },
          setoresValues: {
            values: setoresValues,
            apiFieldName: 'SetorId',
            alwaysArray: false,
          },
          areaGroupsValues: {
            values: areaGroupsValues,
            apiFieldName: 'GrupoAreaId',
            alwaysArray: false,
          },
          areaSubgroupsValues: {
            values: areaSubgroupsValues,
            apiFieldName: 'SubGrupoAreaId',
            alwaysArray: false,
          },
          areasValues: {
            values: areasValues,
            apiFieldName: 'AreaId',
            alwaysArray: false,
          },
          equipmentTypesValues: {
            values: equipmentTypesValues,
            apiFieldName: 'TipoEquipamentoId',
            alwaysArray: false,
          },
          equipmentValues: {
            values: equipmentValues,
            apiFieldName: 'EquipamentoId',
            alwaysArray: false,
          },
        });

        Sentry.logger.info(`Buscando tarefas da agenda para a visão ${isWeek ? 'Semana' : 'Dia'}`, empresaAttrs);
        const response = await AgendaService.getAgendaTasks(apiParams);

        if (isMountedRef.current && response?.data && Array.isArray(response.data)) {
          const transformedEvents = transformAgendaTasksToEvents(response.data as AgendaTask[]);
          setEvents(transformedEvents);
        }
      } catch (error) {
        if (isMountedRef.current) {
          console.error('Erro ao buscar tarefas da agenda:', error);
          Sentry.captureException(error, {
            tags: { page: 'agenda', action: 'fetch_tarefas', empresa: headerFilters?.companies?.id },
          });
          setEvents({});
        }
      } finally {
        if (isMountedRef.current) setIsLoadingEvents(false);
      }
    },
    [
      type,
      view,
      planosValues,
      executoresValues,
      setoresValues,
      allMaintenanceUnitsValues,
      areaGroupsValues,
      areaSubgroupsValues,
      areasValues,
      equipmentTypesValues,
      equipmentValues,
      headerFilters?.companies?.id,
      headerFilters?.units?.id,
      globalUnitGroupValues,
      globalUnitSubgroupValues,
      globalStateValues,
      globalCityValues,
      effectiveDate,
    ],
  );

  useEffect(() => {
    const isWeekView = view === CalendarView.Semana;
    isWeekViewRef.current = isWeekView;
    fetchAgendaTasks(isWeekView);
  }, [view]);

  useEffect(() => {
    if (planosModalOpen || ambientesEquipamentosModalOpen) {
      return;
    }

    const fetchKey = JSON.stringify({
      currentDateString,
      type,
      planosValues,
      executoresValues,
      setoresValues,
      maintenanceUnitsValues,
      areaGroupsValues,
      areaSubgroupsValues,
      areasValues,
      equipmentTypesValues,
      equipmentValues,
      companyId: headerFilters?.companies?.id,
      unitId: headerFilters?.units?.id,
      unitGroupId: globalUnitGroupValues.length > 0 ? globalUnitGroupValues[0] : null,
      unitSubgroupId: globalUnitSubgroupValues.length > 0 ? globalUnitSubgroupValues[0] : null,
      stateId: globalStateValues.length > 0 ? globalStateValues[0] : null,
      cityId: globalCityValues.length > 0 ? globalCityValues[0] : null,
      isWeekView: isWeekViewRef.current,
    });

    if (lastFetchParamsRef.current === fetchKey) {
      return;
    }

    const filtersKey = JSON.stringify({
      type,
      planosValues,
      executoresValues,
      setoresValues,
      maintenanceUnitsValues,
      areaGroupsValues,
      areaSubgroupsValues,
      areasValues,
      equipmentTypesValues,
      equipmentValues,
      companyId: headerFilters?.companies?.id,
      unitId: headerFilters?.units?.id,
      unitGroupId: globalUnitGroupValues.length > 0 ? globalUnitGroupValues[0] : null,
      unitSubgroupId: globalUnitSubgroupValues.length > 0 ? globalUnitSubgroupValues[0] : null,
      stateId: globalStateValues.length > 0 ? globalStateValues[0] : null,
      cityId: globalCityValues.length > 0 ? globalCityValues[0] : null,
      isWeekView: isWeekViewRef.current,
    });
    const onlyDateChanged = previousFiltersKeyRef.current === filtersKey;

    lastFetchParamsRef.current = fetchKey;
    previousFiltersKeyRef.current = filtersKey;
    const isWeekView = view === undefined ? true : view === CalendarView.Semana;
    if (!onlyDateChanged && isMountedRef.current) setEvents({});
    fetchAgendaTasks(isWeekView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    planosModalOpen,
    ambientesEquipamentosModalOpen,
    view,
    currentDateString,
    type,
    planosValues,
    executoresValues,
    setoresValues,
    maintenanceUnitsValues,
    areaGroupsValues,
    areaSubgroupsValues,
    areasValues,
    equipmentTypesValues,
    equipmentValues,
    headerFilters?.companies?.id,
    headerFilters?.units?.id,
    globalUnitGroupValues,
    globalUnitSubgroupValues,
    globalStateValues,
    globalCityValues,
    globalSystemValues,
  ]);

  useEffect(() => {
    if (!executoresSetoresModalOpen) {
      initFromGlobalDoneForExecutoresSetoresRef.current = false;
      return;
    }
    setTempExecutores(executores || []);
    setTempSetores(setores || []);
  }, [executoresSetoresModalOpen, executores, setores]);

  useEffect(() => {
    if (planosModalOpen) {
      setTempPlanos(planos || []);
    }
  }, [planosModalOpen, planos]);

  useEffect(() => {
    if (planosModalOpen) {
      fetchActivitiesPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    planosModalOpen,
    maintenanceUnits,
    headerFilters?.companies?.id,
    headerFilters?.units?.id,
    headerFilters?.systems?.id,
    headerFilters?.unitGroups?.id,
    headerFilters?.unitSubgroups?.id,
    headerFilters?.states?.id,
    headerFilters?.cities?.id,
    headerFilters?.providers?.id,
  ]);

  useEffect(() => {
    if (ambientesEquipamentosModalOpen) {
      setTempAreaGroups(areaGroups || []);
      setTempAreaSubgroups(areaSubgroups || []);
      setTempAreas(areas || []);
      setTempEquipmentTypes(equipmentTypes || []);
      setTempEquipment(equipment || []);
    }
  }, [ambientesEquipamentosModalOpen, areaGroups, areaSubgroups, areas, equipmentTypes, equipment]);

  return {
    type,
    setType,
    planos,
    setPlanos,
    planosSelectionLabel,
    executoresSetoresSelectionLabel,
    ambientesEquipamentosSelectionLabel,
    planosValues,
    handlePlanosChange,
    planosOptions,
    ocorrencias,
    setOcorrencias,
    executores,
    setExecutores,
    executoresValues,
    handleExecutoresChange,
    executoresOptions,
    tempExecutoresValues,
    handleTempExecutoresChange,
    setores,
    setSetores,
    setoresValues,
    handleSetoresChange,
    setoresOptions,
    tempSetoresValues,
    handleTempSetoresChange,
    applyExecutoresSetoresFilters,
    clearExecutoresSetoresFilters,
    fields,
    openFilters,
    setOpenFilters,
    executoresSetoresModalOpen,
    setExecutoresSetoresModalOpen,
    onExecutoresSearch,
    ambientesEquipamentosModalOpen,
    setAmbientesEquipamentosModalOpen,
    planosModalOpen,
    setPlanosModalOpen,
    tempPlanosValues,
    handleTempPlanosChange,
    applyPlanosFilters,
    clearPlanosFilters,
    maintenanceUnitsOptions,
    maintenanceUnitsValues,
    handleMaintenanceUnitsChange,
    areaGroupsOptions,
    areaGroupsValues,
    handleAreaGroupsChange,
    tempAreaGroupsValues,
    handleTempAreaGroupsChange,
    areaSubgroupsOptions,
    areaSubgroupsValues,
    handleAreaSubgroupsChange,
    tempAreaSubgroupsValues,
    handleTempAreaSubgroupsChange,
    areasOptions,
    areasValues,
    handleAreasChange,
    tempAreasValues,
    handleTempAreasChange,
    equipmentTypesOptions,
    equipmentTypesValues,
    handleEquipmentTypesChange,
    tempEquipmentTypesValues,
    handleTempEquipmentTypesChange,
    equipmentOptions,
    equipmentValues,
    handleEquipmentChange,
    tempEquipmentValues,
    handleTempEquipmentChange,
    applyAmbientesEquipamentosFilters,
    clearAmbientesEquipamentosFilters,
    hasActiveFilters,
    clearAllFilters,
    events,
    isLoadingEvents,
    fetchAgendaTasks,
    setWeekView,
    loadMoreAreas,
    searchArea,
    loadMoreEquipamentos,
    searchEquipamento,
  };
};
